import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  BillVm,
  BillListResponse,
  BillListItem,
  BillVendor,
  BillProject,
  BillItem,
  BillTaxRate,
  CostLocation,
  ProfitCenter,
  Department,
  BillAccount,
  BillLine,
  ApiResponse,
} from '@/api/types'
import { calendarFromApi, calendarToday } from '@/lib/calendarDate'
import { appendSortDir, type ListSortDir } from '@/lib/listSort'

/**
 * Purchase Bills service
 */
export class BillsService {
  /**
   * Get paginated list of bills for a branch
   */
  async getBills(
    branchId: number,
    pageNumber: number,
    pageSize: number,
    sortDir: ListSortDir = 'desc'
  ): Promise<BillListResponse> {
    const url = appendSortDir(endpoints.bills.getAll(branchId, pageNumber, pageSize), sortDir)
    const response = await apiClient.get<ApiResponse<BillListResponse> | BillListResponse | any>(url)
    const payload = response.data

    if (payload && typeof payload === 'object') {
      if ('success' in payload && 'data' in payload) {
        return payload.data as BillListResponse
      }

      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: payload.data as BillListItem[],
        }
      }
    }

    if (Array.isArray(payload)) {
      return {
        totalRecords: payload.length,
        pageNumber,
        pageSize,
        data: payload as BillListItem[],
      }
    }

    return {
      totalRecords: payload?.totalRecords ?? 0,
      pageNumber: payload?.pageNumber ?? pageNumber,
      pageSize: payload?.pageSize ?? pageSize,
      data: payload?.data ?? [],
    }
  }

  /**
   * Get bill by ID with lines
   * Calls two separate endpoints:
   * 1. GetBillById - for bill header data (only needs billId)
   * 2. GetBillLinesByBillId - for bill lines (needs branchId and billId)
   */
  async getBillById(branchId: number, billId: number): Promise<BillVm> {
    try {
      // Call both endpoints in parallel
      const [billHeaderResponse, billLinesResponse] = await Promise.all([
        apiClient.get<any>(endpoints.bills.getById(billId)),
        apiClient.get<any>(endpoints.bills.getBillLines(branchId, billId))
      ])
      
      console.log('🔵 [BillsService.getBillById] Calling GetBillById:', endpoints.bills.getById(billId))
      console.log('🔵 [BillsService.getBillById] Calling GetBillLinesByBillId:', endpoints.bills.getBillLines(branchId, billId))
      
      console.log('🔵 [BillsService.getBillById] Bill Header Response:', billHeaderResponse.data)
      console.log('🔵 [BillsService.getBillById] Bill Lines Response:', billLinesResponse.data)
      
      // Extract bill header data
      let billData: any = null
      const headerResponseData = billHeaderResponse.data
      
      // Helper function to extract data from nested structures
      const extractData = (data: any): any => {
        if (!data || typeof data !== 'object') return data
        
        if ('success' in data && 'data' in data) {
          return extractData(data.data)
        }
        
        if ('data' in data) {
          return extractData(data.data)
        }
        
        return data
      }
      
      const extractedHeaderData = extractData(headerResponseData)
      console.log('🔵 [BillsService.getBillById] Extracted Header Data:', JSON.stringify(extractedHeaderData, null, 2))
      
      // Handle bill header response
      if (extractedHeaderData && typeof extractedHeaderData === 'object' && !Array.isArray(extractedHeaderData)) {
        billData = extractedHeaderData
      } else if (Array.isArray(extractedHeaderData) && extractedHeaderData.length > 0) {
        billData = extractedHeaderData[0]
      } else {
        console.warn('⚠️ [BillsService.getBillById] Invalid bill header data, using empty object')
        billData = {}
      }
      
      // Extract bill lines data
      let billLines: any[] = []
      const linesResponseData = billLinesResponse.data
      const extractedLinesData = extractData(linesResponseData)
      console.log('🔵 [BillsService.getBillById] Extracted Lines Data:', JSON.stringify(extractedLinesData, null, 2))
      
      // Handle bill lines response - it's an array
      if (Array.isArray(extractedLinesData)) {
        billLines = extractedLinesData
      } else if (extractedLinesData && typeof extractedLinesData === 'object') {
        // Check if lines are nested in a property
        if ('billLines' in extractedLinesData && Array.isArray(extractedLinesData.billLines)) {
          billLines = extractedLinesData.billLines
        } else if ('billsLines' in extractedLinesData && Array.isArray(extractedLinesData.billsLines)) {
          billLines = extractedLinesData.billsLines
        }
      }
      
      console.log('🔵 [BillsService.getBillById] Final billData keys:', Object.keys(billData))
      console.log('🔵 [BillsService.getBillById] Final billLines count:', billLines.length)
      if (billLines.length > 0) {
        console.log('🔵 [BillsService.getBillById] First bill line:', billLines[0])
      }
      
      return this.mapBillData(billData, branchId, billLines)
    } catch (error: any) {
      console.error('❌ [BillsService.getBillById] Error:', error)
      console.error('❌ [BillsService.getBillById] Error Response:', error.response)
      console.error('❌ [BillsService.getBillById] Error Data:', error.response?.data)
      throw error
    }
  }

  /**
   * Map API response to BillVm interface
   * API Response Structure:
   * {
   *   "billId": 5,
   *   "billDate": "2025-12-06T00:00:00",
   *   "vendorId": 12,
   *   "dueDate": "2025-12-09T00:00:00",
   *   "subTotal": 100,
   *   "totalTax": 15,
   *   "total": 115,
   *   "note": "test",
   *   "vendorBillDate": "2025-12-10T19:00:00Z",
   *   "projectId": 3,
   *   "destinationAccount": 1157,
   *   ...
   * }
   */
  private mapBillData(billData: any, branchId: number, billLines?: any[]): BillVm {
    // Ensure billData is an object
    if (!billData || typeof billData !== 'object') {
      console.warn('⚠️ [BillsService.mapBillData] billData is invalid, using empty object')
      billData = {}
    }
    
    // Map bill lines if provided, otherwise use from billData
    const lines = billLines || billData.billLines || billData.billsLines || []
    
    // Map each bill line to ensure proper structure
    const mappedLines: BillLine[] = []
    if (Array.isArray(lines)) {
      for (const line of lines) {
        if (!line || typeof line !== 'object') {
          console.warn(`⚠️ [BillsService.mapBillData] Invalid line:`, line)
          continue
        }
        
        const lineId = line.lineId ?? line.LineId ?? line.id ?? line.Id ?? 0
        const billId = line.billId ?? line.BillId ?? billData.billId ?? billData.BillId ?? 0
        const itemId = line.itemId ?? line.ItemId ?? 0
        const description = line.description ?? line.Description ?? ''
        const qty = Number(line.qty ?? line.Qty ?? 0) || 0
        const unitPrice = Number(line.unitPrice ?? line.UnitPrice ?? 0) || 0
        const itemPrice = Number(line.itemPrice ?? line.ItemPrice ?? 0) || 0
        const discountPercent = Number(line.discountPercent ?? line.DiscountPercent ?? 0) || 0
        const discountAmount = Number(line.discountAmount ?? line.DiscountAmount ?? 0) || 0
        const taxableAmount = Number(line.taxableAmount ?? line.TaxableAmount ?? 0) || 0
        const taxRateId = Number(line.taxRateId ?? line.TaxRateId ?? 0) || 0
        const taxRate = Number(line.taxRate ?? line.TaxRate ?? line.taxPercent ?? line.TaxPercent ?? 0) || 0
        const taxAmount = Number(line.taxAmount ?? line.TaxAmount ?? 0) || 0
        const lineAmount = Number(line.lineAmount ?? line.LineAmount ?? 0) || 0
        const debitAccount = Number(line.debitAccount ?? line.DebitAccount ?? 0) || 0
        const departmentId = Number(line.departmentId ?? line.DepartmentId ?? 0) || 0
        const costProfitCenterId = Number(line.costProfitCenterId ?? line.CostProfitCenterId ?? 0) || 0
        const costLocationId = Number(line.costLocationId ?? line.CostLocationId ?? 0) || 0
        const taxPercent = Number(line.taxPercent ?? line.TaxPercent ?? taxRate) || 0
        const isDeleted = line.isDeleted ?? line.IsDeleted ?? 'false'

        mappedLines.push({
          lineId,
          billId,
          itemId: Number(itemId) || 0,
          description: String(description ?? ''),
          qty,
          unitPrice,
          itemPrice,
          discountPercent,
          discountAmount,
          taxableAmount,
          taxRateId,
          taxRate,
          taxAmount,
          lineAmount,
          debitAccount,
          departmentId,
          costProfitCenterId,
          costLocationId,
          taxPercent,
          isDeleted,
          transactionTypeIdD: line.transactionTypeIdD ?? line.TransactionTypeIdD,
        })
      }
    }
    
    console.log('🔵 [BillsService.mapBillData] Mapped bill lines:', mappedLines.length, 'items')
    if (mappedLines.length > 0) {
      console.log('🔵 [BillsService.mapBillData] Sample line:', mappedLines[0])
    }
    
    // Format date from ISO string to YYYY-MM-DD format for input fields
    const formatDateForInput = (dateString?: string): string => calendarFromApi(dateString)
    
    // Get bill ID from various possible fields
    const billId = billData.billId || billData.id || 0
    
    // Map vendorBillDate - handle different formats
    let vendorBillDate = ''
    if (billData.vendorBillDate) {
      vendorBillDate = formatDateForInput(billData.vendorBillDate)
    } else if (billData.billDate) {
      vendorBillDate = formatDateForInput(billData.billDate)
    }
    
    return {
      billId: billId,
      billDate: formatDateForInput(billData.billDate) || calendarToday(),
      vendorId: billData.vendorId || 0,
      projectId: billData.projectId || 0,
      dueDate: formatDateForInput(billData.dueDate) || '',
      subTotal: Number(billData.subTotal) || 0,
      totalTax: Number(billData.totalTax) || 0,
      total: Number(billData.total) || 0,
      note: billData.note || '',
      branchId: billData.branchId || branchId,
      vendorBillDate: vendorBillDate || calendarToday(),
      ipAddress: billData.ipAddress || '',
      billLines: mappedLines,
      retentionPercent: billData.retentionPercent,
      totalRetentionAmount: billData.totalRetentionAmount,
      transactionModuleId: billData.transactionModuleId,
      transactionTypeIdD: billData.transactionTypeIdD,
      isPosted: billData.isPosted === true || billData.isPosted === 1,
      billNo: billData.billNo || billData.BillNo || '',
      vendorNameEn: billData.vendorNameEn || billData.VendorNameEn || '',
      totalDiscount: Number(billData.totalDiscount ?? billData.TotalDiscount) || 0,
      refNo: billData.refNo || billData.RefNo || '',
    }
  }

  /**
   * Get all vendors for a branch
   */
  async getAllVendors(branchId: number): Promise<BillVendor[]> {
    const url = endpoints.bills.getAllVendors(branchId)
    const response = await apiClient.get<ApiResponse<BillVendor[]> | BillVendor[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get all projects for a branch
   */
  async getProjects(branchId: number): Promise<BillProject[]> {
    const url = endpoints.bills.getProject(branchId)
    const response = await apiClient.get<ApiResponse<BillProject[]> | BillProject[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get all items for a branch
   */
  async getItems(branchId: number): Promise<BillItem[]> {
    const url = endpoints.bills.getItemList(branchId)
    const response = await apiClient.get<ApiResponse<BillItem[]> | BillItem[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get all tax rates for a branch
   */
  async getTaxRates(branchId: number): Promise<BillTaxRate[]> {
    const url = endpoints.bills.getTaxRates(branchId)
    const response = await apiClient.get<ApiResponse<BillTaxRate[]> | BillTaxRate[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get all cost locations
   */
  async getCostLocations(): Promise<CostLocation[]> {
    const url = endpoints.bills.getCostLocation
    const response = await apiClient.get<ApiResponse<CostLocation[]> | CostLocation[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get all profit centers
   */
  async getProfitCenters(): Promise<ProfitCenter[]> {
    const url = endpoints.bills.getProfitCenter
    const response = await apiClient.get<ApiResponse<ProfitCenter[]> | ProfitCenter[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get all departments
   */
  async getDepartments(): Promise<Department[]> {
    const url = endpoints.bills.getDepartments
    const response = await apiClient.get<ApiResponse<Department[]> | Department[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    
    if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get all accounts for a branch
   */
  async getAllAccounts(branchId: number): Promise<BillAccount[]> {
    try {
      const url = endpoints.bills.getAllAccounts(branchId)
      console.log('🔵 [BillsService.getAllAccounts] Calling:', url, 'branchId:', branchId)
      const response = await apiClient.get<ApiResponse<BillAccount[]> | BillAccount[] | any>(url)
      console.log('🔵 [BillsService.getAllAccounts] Full Response:', response)
      console.log('🔵 [BillsService.getAllAccounts] Response Data:', response.data)
      
      const payload = response.data
      
      // Helper to map account data
      const mapAccount = (acc: any): BillAccount => {
        return {
          accountId: acc.accountId || acc.id || 0,
          id: acc.id || acc.accountId || 0,
          accountCode: acc.accountCode || acc.code || '',
          accountName: acc.accountName || acc.accountNameEn || acc.nameEn || acc.name || '',
          accountNameEn: acc.accountNameEn || acc.nameEn || acc.name || '',
          accountNameAr: acc.accountNameAr || acc.nameAr || '',
        }
      }
      
      // Handle different response formats
      if (Array.isArray(payload)) {
        console.log('🔵 [BillsService.getAllAccounts] Returning array:', payload.length, 'items')
        return payload.map(mapAccount)
      }
      
      if (payload && typeof payload === 'object') {
        // Check if wrapped in success/data structure
        if ('success' in payload && 'data' in payload) {
          const data = payload.data
          if (Array.isArray(data)) {
            console.log('🔵 [BillsService.getAllAccounts] Returning from success.data:', data.length, 'items')
            return data.map(mapAccount)
          }
        }
        
        // Check if data is in 'data' property
        if ('data' in payload && Array.isArray(payload.data)) {
          console.log('🔵 [BillsService.getAllAccounts] Returning from data property:', payload.data.length, 'items')
          return payload.data.map(mapAccount)
        }
        
        // Check if it's a single account object
        if ('id' in payload || 'accountId' in payload) {
          console.log('🔵 [BillsService.getAllAccounts] Returning single account')
          return [mapAccount(payload)]
        }
      }
      
      console.warn('⚠️ [BillsService.getAllAccounts] Unexpected response format:', payload)
      return []
    } catch (error: any) {
      console.error('❌ [BillsService.getAllAccounts] Error:', error)
      console.error('❌ [BillsService.getAllAccounts] Error Response:', error.response)
      console.error('❌ [BillsService.getAllAccounts] Error Data:', error.response?.data)
      throw error
    }
  }

  /**
   * Save new bill
   */
  async saveBill(bill: BillVm): Promise<any> {
    const url = endpoints.bills.insert
    try {
      console.log('========== API PAYLOAD (Bill Insert) ==========')
      console.log(JSON.stringify(bill, null, 2))
      console.log('===============================================')
      const response = await apiClient.post<ApiResponse<any> | any>(url, bill)
      console.log('========== API RESPONSE (Bill Insert) ==========')
      console.log('Raw response:', response)
      console.log('Response data:', response.data)
      console.log('===============================================')
      return response.data
    } catch (error: any) {
      console.error('========== BILL INSERT ERROR DETAILS ==========')
      console.error('Error:', error)
      console.error('Error Response:', error?.response)
      console.error('Error Response Data:', error?.response?.data)
      console.error('Error Response Status:', error?.response?.status)
      console.error('Error Response Headers:', error?.response?.headers)
      console.error('Payload that caused error:', JSON.stringify(bill, null, 2))
      console.error('===============================================')
      throw error
    }
  }

  /**
   * Update existing bill
   */
  async updateBill(bill: BillVm): Promise<any> {
    const url = endpoints.bills.update
    try {
      console.log('========== API PAYLOAD (Bill Update) ==========')
      console.log(JSON.stringify(bill, null, 2))
      console.log('==============================================')
      const response = await apiClient.put<ApiResponse<any> | any>(url, bill)
      console.log('========== API RESPONSE (Bill Update) ==========')
      console.log('Raw response:', response)
      console.log('Response data:', response.data)
      console.log('==============================================')
      return response.data
    } catch (error: any) {
      console.error('========== BILL UPDATE ERROR DETAILS ==========')
      console.error('Error:', error)
      console.error('Error Response:', error?.response)
      console.error('Error Response Data:', error?.response?.data)
      console.error('Error Response Status:', error?.response?.status)
      console.error('Error Response Headers:', error?.response?.headers)
      console.error('Payload that caused error:', JSON.stringify(bill, null, 2))
      console.error('===============================================')
      throw error
    }
  }

  async postBill(id: number, ipAddress?: string): Promise<any> {
    const url = endpoints.bills.postData(id, ipAddress)
    const response = await apiClient.post<any>(url)
    return response.data
  }
}

// Export singleton instance
export const billsService = new BillsService()

